import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import BeneficiaryName from '@UI/Pages/Beneficiaries/Shared/BeneficiaryName';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ItemBreadcrumb } from '@verclam/breadcrumbs';
import { useSelector } from '@verclam/synapse';

export type SubjectBreadcrumbItem = {
	readonly label: string;
	readonly url?: string;
};

type Props = {
	readonly subject: SubjectAddress;
	readonly items?: readonly SubjectBreadcrumbItem[];
	readonly onDossierLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

const FALLBACK_LABEL = 'Dossier';

/**
 * Declares `<dossier> › …items` for the mounted screen; a renderer elsewhere draws it.
 * The name comes from the dossier already in the store, never from a request: until it matches
 * `subject` the dossier entry reads « Dossier ». The dossier entry is the current page, hence
 * not a link, when `items` is empty.
 *
 * Every entry sits in one keyed fragment: the library re-appends an entry whose props change at the
 * end of the trail, so a name arriving late would reorder it. Changing the key remounts them together,
 * in order.
 */
const SubjectBreadcrumb: React.FC<Props> = ({ subject, items = [], onDossierLinkClick }) => {
	const { typeKey, subjectId, isBeneficiary } = subject;

	const firstName = useSelector((state) => {
		const beneficiary = state.beneficiariesStore.beneficiary;
		return isBeneficiary && beneficiary !== null && beneficiary.id === subjectId
			? beneficiary.firstName
			: null;
	});
	const lastName = useSelector((state) => {
		const beneficiary = state.beneficiariesStore.beneficiary;
		return isBeneficiary && beneficiary !== null && beneficiary.id === subjectId
			? beneficiary.lastName
			: null;
	});
	const title = useSelector((state) => {
		const loaded = state.subjectsStore.subject;
		return !isBeneficiary && loaded !== null && loaded.id === subjectId ? loaded.title : null;
	});

	const dossierLabel = React.useMemo(() => {
		if (firstName !== null && lastName !== null) {
			return `${lastName} ${firstName}`;
		}

		return title ?? FALLBACK_LABEL;
	}, [firstName, lastName, title]);
	const isCurrent = items.length === 0;

	const dossierContent = React.useMemo(() => {
		const name =
			firstName !== null && lastName !== null ? (
				<BeneficiaryName firstName={firstName} lastName={lastName} />
			) : (
				dossierLabel
			);

		if (isCurrent) {
			return <span>{name}</span>;
		}

		return (
			<Link to={buildSubjectPath(subject)} onClick={onDossierLinkClick}>
				{name}
			</Link>
		);
	}, [firstName, lastName, dossierLabel, isCurrent, subject, onDossierLinkClick]);

	const fragmentKey = `${typeKey}:${subjectId}|${dossierLabel}|${items.map((item) => `${item.label}@${item.url ?? ''}`).join('|')}`;

	return (
		<React.Fragment key={fragmentKey}>
			<ItemBreadcrumb label={dossierLabel}>{dossierContent}</ItemBreadcrumb>
			{items.map((item) => (
				<ItemBreadcrumb key={`${item.label}@${item.url ?? ''}`} label={item.label} url={item.url} />
			))}
		</React.Fragment>
	);
};

export default SubjectBreadcrumb;
